import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";
import { Loader2, Save, Trash2, Plus, Shield, User as UserIcon, Users, Code2, Building2, Camera, Clock } from "lucide-react";
import TeamTab from "@/pages/settings/TeamTab";
// Developer API is temporarily disabled (shown as "Coming soon"). Keep the import
// commented so the full tab code stays in the repo, ready to re-enable later.
// import DeveloperTab from "@/pages/settings/DeveloperTab";

const TABS = [
  { id: "profile", label: "Profile", icon: UserIcon },
  { id: "workspace", label: "Workspace", icon: Building2 },
  { id: "team", label: "Team", icon: Users },
  { id: "developer", label: "Developer", icon: Code2, soon: true },
  { id: "blocklist", label: "Blocklist", icon: Shield },
];

export default function Settings() {
  const [tab, setTab] = useState("profile");

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-1.5">
          Settings
        </div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
          Account settings
        </h1>
      </div>

      <div className="flex items-center gap-1 border-b border-lm-border mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? "border-lm-primary text-lm-primary"
                  : "border-transparent text-lm-text2 hover:text-lm-text"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {t.soon && (
                <span className="ml-1 text-[9px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-lm-tint2 text-lm-primary">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "profile" && <ProfileTab />}
      {tab === "workspace" && <WorkspaceTab />}
      {tab === "team" && <TeamTab />}
      {/* Developer API temporarily disabled — show "Coming soon". Re-enable by
          restoring the import above and swapping this back to <DeveloperTab />. */}
      {tab === "developer" && <ComingSoonTab />}
      {tab === "blocklist" && <BlocklistTab />}
    </div>
  );
}

// Placeholder shown in place of the Developer API tab while it's disabled.
function ComingSoonTab() {
  return (
    <div className="max-w-xl bg-lm-surface border border-lm-border rounded-lg p-10 text-center" data-testid="developer-tab-coming-soon">
      <div className="w-14 h-14 rounded-2xl bg-lm-tint2 flex items-center justify-center mx-auto mb-5">
        <Code2 className="w-6 h-6 text-lm-primary" />
      </div>
      <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-lm-primary bg-lm-tint2 px-2.5 py-1 rounded-full mb-3">
        <Clock className="w-3.5 h-3.5" /> Coming soon
      </div>
      <h2 className="text-xl font-display font-semibold mb-1.5">Developer API</h2>
      <p className="text-sm text-lm-text2 max-w-sm mx-auto">
        API keys and programmatic access are on the way. You'll be able to run scans
        and pull leads straight from your own code. Stay tuned.
      </p>
    </div>
  );
}

function ProfileTab() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  const avatarSrc =
    user?.picture ||
    `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(user?.email || user?.user_id || user?.name || "user")}&backgroundType=gradientLinear`;
  const initial = (user?.name || "?").charAt(0).toUpperCase();

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setUploading(true);
    try {
      const dataUrl = await resizeImage(file, 400, 400);
      const r = await api.patch("/auth/me", { picture: dataUrl });
      setUser(r.data);
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo.");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      const r = await api.patch("/auth/me", { picture: "" });
      setUser(r.data);
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove photo.");
    } finally {
      setUploading(false);
    }
  };

  const cleanEmail = (email || "").trim().toLowerCase();
  const dirty = name.trim() !== (user?.name || "") || cleanEmail !== (user?.email || "").toLowerCase();

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (name.trim() !== (user?.name || "")) body.name = name.trim();
      if (cleanEmail !== (user?.email || "").toLowerCase()) body.email = cleanEmail;
      const r = await api.patch("/auth/me", body);
      setUser(r.data);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl bg-lm-surface border border-lm-border rounded-lg p-6 md:p-8">
      {/* Avatar */}
      <div className="flex items-center gap-4 mb-8">
        <div className="relative shrink-0">
          <div className="w-20 h-20 rounded-full border border-lm-border bg-lm-surface overflow-hidden flex items-center justify-center relative">
            <span className="absolute text-2xl font-display font-bold text-lm-primary">{initial}</span>
            <img src={avatarSrc} alt={user?.name || "You"} className="relative w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Change profile photo"
            className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-lm-primary text-white flex items-center justify-center shadow hover:bg-lm-primaryHover disabled:opacity-70"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} />
        </div>
        <div>
          <h2 className="text-lg font-display font-semibold">Profile photo</h2>
          <p className="text-sm text-lm-text2">
            Shown in the top bar. {user?.picture ? "" : "Currently an auto-generated avatar."}
          </p>
          {user?.picture && (
            <button type="button" onClick={removePhoto} disabled={uploading} className="text-xs text-red-600 hover:underline mt-1">
              Remove photo
            </button>
          )}
        </div>
      </div>

      <label className="block text-sm font-medium text-lm-text mb-1.5">Full name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className={profileInputCls} placeholder="Your name" />

      <label className="block text-sm font-medium text-lm-text mb-1.5 mt-4">Email</label>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={profileInputCls} placeholder="you@company.com" />
      <p className="text-xs text-lm-text3 mt-1.5">Used to sign in and for account emails.</p>

      <div className="mt-6 flex items-center justify-end">
        <button
          onClick={save}
          disabled={saving || !dirty || !name.trim() || !email.trim()}
          className="inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-medium hover:bg-lm-primaryHover disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const profileInputCls =
  "w-full border border-lm-border rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary";

// Downscale a picked image client-side → small JPEG data URL.
function resizeImage(file, maxW, maxH, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function WorkspaceTab() {
  const { workspace, refreshWorkspace } = useAuth();
  const [name, setName] = useState(workspace?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(workspace?.name || "");
  }, [workspace]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/workspaces/current", { name });
      await refreshWorkspace();
      toast.success("Workspace updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update workspace.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl bg-lm-surface border border-lm-border rounded-lg p-6 md:p-8">
      <h2 className="text-lg font-display font-semibold mb-1">Workspace name</h2>
      <p className="text-sm text-lm-text2 mb-4">Displayed at the top of your app and on invoices.</p>
      <input
        data-testid={TID.settingsWorkspaceNameInput}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full border border-lm-border rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
      />
      <div className="mt-5 flex items-center justify-end">
        <button
          data-testid={TID.settingsSaveBtn}
          onClick={save}
          disabled={saving || !name.trim()}
          className="inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-medium hover:bg-lm-primaryHover disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const BLOCK_TYPES = [
  { id: "author", label: "Author / Username" },
  { id: "domain", label: "Domain" },
  { id: "keyword", label: "Keyword" },
];

function BlocklistTab() {
  // Workspace-scoped list, previously fetched once per mount — safe only while
  // a switch reloaded the page. Keyed on the epoch now.
  const { workspaceEpoch } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [type, setType] = useState("author");
  const [value, setValue] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/blocklist");
      setItems(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load blocklist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    load();
    // `load` is a plain function redefined every render; listing it would loop.
    // The epoch is the only thing that should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceEpoch]);

  const add = async () => {
    const v = value.trim();
    if (!v) return;
    setAdding(true);
    try {
      const r = await api.post("/blocklist", { type, value: v });
      // Prepend if new (backend returns existing if idempotent)
      setItems((it) => {
        const exists = it.find((x) => x.entry_id === r.data.entry_id);
        return exists ? it : [r.data, ...it];
      });
      setValue("");
      toast.success("Blocklist entry added.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add entry.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove "${entry.value}" from blocklist?`)) return;
    const prev = items;
    setItems((it) => it.filter((x) => x.entry_id !== entry.entry_id));
    try {
      await api.delete(`/blocklist/${entry.entry_id}`);
      toast.success("Entry removed.");
    } catch (e) {
      setItems(prev);
      toast.error("Could not remove entry.");
    }
  };

  const grouped = BLOCK_TYPES.map((bt) => ({
    ...bt,
    items: items.filter((x) => x.type === bt.id),
  }));

  return (
    <div className="max-w-3xl">
      <div className="bg-lm-surface border border-lm-border rounded-lg p-6 md:p-8 mb-6">
        <h2 className="text-lg font-display font-semibold mb-1">Filter out noise</h2>
        <p className="text-sm text-lm-text2 mb-5">
          Signals from these authors, domains or containing these keywords will be hidden from future scans.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            data-testid="blocklist-type-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border border-lm-border rounded-lg px-3 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          >
            {BLOCK_TYPES.map((bt) => (
              <option key={bt.id} value={bt.id}>
                {bt.label}
              </option>
            ))}
          </select>
          <input
            data-testid="blocklist-value-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={
              type === "domain"
                ? "e.g. spam-site.com"
                : type === "keyword"
                ? "e.g. crypto"
                : "e.g. @some_user"
            }
            className="flex-1 border border-lm-border rounded-lg px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          />
          <button
            data-testid="blocklist-add-btn"
            onClick={add}
            disabled={adding || !value.trim()}
            className="inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-4 py-2.5 font-medium text-sm hover:bg-lm-primaryHover disabled:opacity-60"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-lm-text2 text-sm py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-lm-border bg-lm-surface p-10 text-center">
          <div className="w-12 h-12 rounded-lg bg-lm-tint2 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-5 h-5 text-lm-primary" />
          </div>
          <h3 className="text-lg font-display font-semibold mb-1.5">Blocklist is empty</h3>
          <p className="text-sm text-lm-text2 max-w-md mx-auto">
            Add authors, domains or keywords above to hide their signals from every future scan.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="blocklist-list">
          {grouped.map((g) => (
            <div key={g.id} className="bg-lm-surface border border-lm-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-mono uppercase tracking-widest text-lm-text2">
                  {g.label}
                </h3>
                <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-lm-tint2 text-lm-primary">
                  {g.items.length}
                </span>
              </div>
              <div className="flex flex-col gap-1.5 min-h-[40px]">
                {g.items.length === 0 && (
                  <div className="text-xs text-lm-text3 italic py-2">None yet</div>
                )}
                {g.items.map((entry) => (
                  <div
                    key={entry.entry_id}
                    data-testid="blocklist-item"
                    className="group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-lm-tint/30 border border-lm-border/60 text-xs"
                  >
                    <span className="font-mono truncate">{entry.value}</span>
                    <button
                      onClick={() => remove(entry)}
                      data-testid="blocklist-remove-btn"
                      className="p-1 text-lm-text3 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remove ${entry.value}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
